import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { auth } from '../../firebase/config';
import {
  getUserOrganization,
  getCompetitors,
  createCompetitor,
  deleteCompetitor,
  updateCompetitor
} from '../../firebase/firestore';
import {
  Users,
  Plus,
  Trash2,
  Edit2,
  X,
  Check,
  AlertCircle,
  ExternalLink,
  Globe
} from 'lucide-react';

function Competitors() {
  const [competitors, setCompetitors] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({
    name: '',
    domain: '',
    website: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Get user's organization
      const org = await getUserOrganization(user.uid);
      if (!org) {
        console.log('No organization found');
        return;
      }
      setOrganization(org);

      // Get competitors for organization
      const comps = await getCompetitors(org.id);
      setCompetitors(comps);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleAddCompetitor = async () => {
    if (!newCompetitor.name.trim() || !newCompetitor.domain.trim() || !organization) return;

    try {
      const user = auth.currentUser;
      if (!user) return;

      const competitor = await createCompetitor(organization.id, {
        name: newCompetitor.name.trim(),
        domain: newCompetitor.domain.trim(),
        website: newCompetitor.website.trim(),
        userId: user.uid,
        active: true
      });

      setCompetitors([competitor, ...competitors]);
      setNewCompetitor({ name: '', domain: '', website: '' });
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding competitor:', error);
    }
  };

  const handleDeleteCompetitor = async (id) => {
    try {
      await deleteCompetitor(id);
      setCompetitors(competitors.filter(comp => comp.id !== id));
    } catch (error) {
      console.error('Error deleting competitor:', error);
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    try {
      await updateCompetitor(id, { active: !currentActive });
      setCompetitors(competitors.map(comp =>
        comp.id === id ? { ...comp, active: !currentActive } : comp
      ));
    } catch (error) {
      console.error('Error toggling competitor status:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Competitors</h1>
          <p className="text-gray-400">
            Track and compare your GEO performance against competitors
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 px-6 py-3 rounded-lg hover:shadow-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          <span>Add Competitor</span>
        </button>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <p className="text-sm text-gray-400 mb-2">Total Competitors</p>
          <h3 className="text-3xl font-bold text-white">{competitors.length}</h3>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <p className="text-sm text-gray-400 mb-2">Active Tracking</p>
          <h3 className="text-3xl font-bold text-white">
            {competitors.filter(c => c.active).length}
          </h3>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <p className="text-sm text-gray-400 mb-2">Avg. Rank Difference</p>
          <h3 className="text-3xl font-bold text-white">-</h3>
        </motion.div>
      </div>

      {/* Competitors List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
      >
        {competitors.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-semibold text-white mb-2">No competitors yet</h3>
            <p className="text-gray-400 mb-6">
              Add competitors to track and compare your GEO performance
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-gradient-to-r from-primary-500 to-secondary-500 px-6 py-3 rounded-lg hover:shadow-lg transition-all inline-flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Add Competitor</span>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {competitors.map((competitor, index) => (
              <motion.div
                key={competitor.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="bg-dark-700/50 border border-white/10 rounded-lg p-6 hover:border-primary-500/30 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-primary-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{competitor.name}</h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-400">
                          <Globe className="w-3 h-3" />
                          <span>{competitor.domain}</span>
                        </div>
                      </div>
                    </div>

                    {competitor.website && (
                      <a
                        href={competitor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-1 text-sm text-primary-400 hover:text-primary-300 transition-colors"
                      >
                        <span>Visit website</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}

                    <div className="mt-4 grid grid-cols-3 gap-4">
                      <div className="bg-dark-800/50 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1">Avg. Position</p>
                        <p className="text-lg font-bold text-white">-</p>
                      </div>
                      <div className="bg-dark-800/50 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1">Citations</p>
                        <p className="text-lg font-bold text-white">-</p>
                      </div>
                      <div className="bg-dark-800/50 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1">Last Checked</p>
                        <p className="text-xs text-gray-400">Never</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-3 ml-6">
                    <button
                      onClick={() => handleToggleActive(competitor.id, competitor.active)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        competitor.active
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                      }`}
                    >
                      {competitor.active ? 'Active' : 'Paused'}
                    </button>

                    <div className="flex items-center space-x-2">
                      <button
                        className="p-2 text-gray-400 hover:text-primary-400 hover:bg-dark-700/50 rounded-lg transition-all"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCompetitor(competitor.id)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-dark-700/50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Add Competitor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-dark-800 border border-white/10 rounded-xl p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Add Competitor</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Competitor Name *
                </label>
                <input
                  type="text"
                  value={newCompetitor.name}
                  onChange={(e) => setNewCompetitor({ ...newCompetitor, name: e.target.value })}
                  placeholder="Acme Corporation"
                  className="w-full bg-dark-700 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Domain *
                </label>
                <input
                  type="text"
                  value={newCompetitor.domain}
                  onChange={(e) => setNewCompetitor({ ...newCompetitor, domain: e.target.value })}
                  placeholder="acme.com"
                  className="w-full bg-dark-700 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <p className="mt-2 text-xs text-gray-400">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Enter the competitor's primary domain (without https://)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Website (Optional)
                </label>
                <input
                  type="url"
                  value={newCompetitor.website}
                  onChange={(e) => setNewCompetitor({ ...newCompetitor, website: e.target.value })}
                  placeholder="https://acme.com"
                  className="w-full bg-dark-700 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCompetitor}
                  disabled={!newCompetitor.name.trim() || !newCompetitor.domain.trim()}
                  className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 px-6 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="w-4 h-4" />
                  <span>Add Competitor</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default Competitors;
